/**
 * Precedent: APR's Disposition Matrix — `apps/portal/web/src/pages/apr/settings/ScoringModel.tsx`
 * (+ the rules it uses from `pages/apr/source-css/app.css`). Cloned, not imported (D-003).
 *
 * SCREEN_MAP #24 — SCO-01. *"APR's own band editor, and the closest thing it has to
 * `ScoringConfig` … More sections, same screen grammar."* What transfers is the precedent's
 * section heading and note type, its input metrics, and its RBAC pattern verbatim: `canModify` computed once (`:60`), checked again in
 * the handler (`:133`), and **the controls not rendered at all rather than disabled** (`:289`,
 * `:661`). That is UI_CONVENTIONS §17.3, and §7b.2 is `erp_administrator` only, preview included.
 *
 * The precedent draws exactly this line, and it is the line followed here: the ACTIONS disappear
 * for a role that may not use them, while the values stay on screen read-only
 * (`ScoringModel.tsx:646-681` renders every band card regardless and hides only Edit and Delete).
 * Hiding the fields as well would leave a Viewer an empty page where the question "what is this
 * initiative scored on?" has a legitimate answer.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════════════╗
 * ║ THE WARNING IS THE POINT OF THIS SCREEN.                                               ║
 * ║                                                                                        ║
 * ║ D-015, in the owner's words: *"No Scoring config should never be frozen. If initiative  ║
 * ║ baseline scoring is changed, then we will need to bring warning message that scoring    ║
 * ║ definition is in place and defined ad that every change in baeline definition would     ║
 * ║ trigger re-scoring."*                                                                  ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════╝
 *
 * So every Save here goes through §7b.2's `?dryRun=1` FIRST — the same endpoint, the same body,
 * the same validation, the same warning, and no write and no audit entry. If the preview comes
 * back with `warnings: []` the change is committed straight away; if it comes back carrying the
 * warning, the dialog shows it BEFORE anything is written, and the person who can judge the
 * consequence decides.
 *
 * **EVERY NUMBER IN THAT DIALOG IS THE SERVER'S, ALREADY RENDERED.** `changedSettings[]` carries
 * `beforeDisplay` and `afterDisplay` put through the audit trail's own formatter, *"so that
 * '60.0% → 55.0%' on the screen and '60.0% → 55.0%' in `audit_entry` are produced by one function
 * and cannot disagree"*; `rescoring` carries how many units re-score and how many change quadrant,
 * MEASURED by scoring the initiative twice rather than estimated. This screen formats none of it.
 * A dialog that worded the change its own way would be the one place an audit trail could be
 * contradicted by the screen that caused the entry.
 *
 * **The warning is a SUCCESS, not a refusal**, and the preview is not a second code path:
 * *"A flag on the write cannot disagree with the write, because it IS the write, stopped one line
 * short of it."* The definition never freezes — there is no un-baseline endpoint and no
 * versioning, so a freeze would lock an administrator out of correcting a threshold they had set
 * wrongly for good.
 *
 * ── THE OTHER THINGS WORTH KNOWING BEFORE EDITING THIS FILE ────────────────────────────
 *
 *  · **SECTIONED SAVES ARE THE CONTRACT, NOT A CONVENIENCE.** §7b.2's PUT has PATCH semantics
 *    precisely because this screen is sectioned: *"a strict PUT would reset the other sixteen
 *    columns to the defaults."* Each Save sends only its own section's fields.
 *  · **THE DEFAULTS ARE NOT A ROW.** An initiative with no `scoring_config` row scores on the
 *    defaults and the payload says so with `source: 'default'` and `id: null` (§7b.1). The screen
 *    shows which of the two is in force, because "these are the defaults" and "somebody chose
 *    these" are different statements about identical numbers.
 *  · **A BAND SET IS 2 TO 10 BANDS, AND IT IS EDITED WHOLE** (D-022, `0109` then `0110`). Each
 *    set is two arrays on the payload — where every band but the last ENDS, and what each band is
 *    WORTH — held here as typed text in `bands`, apart from the scalar `form`, and sent whole on
 *    Save. Add splits the open-ended top band; delete gives a band's counts to the band above.
 *    The boundaries used to be fixed in the scoring package and read from a scored unit's
 *    breakdown, so a new initiative could not name its own bands.
 *  · **THE LOCAL GATES** are the pair that must total 100.0% (`factor_weights_must_total_100`)
 *    and a band set whose ends ascend strictly and whose worth values ascend
 *    (`band_boundaries_must_ascend`, `band_values_must_ascend`). Each is shown live and keeps its
 *    own Save closed; the server refuses the same things by name regardless.
 *
 * ── THE LAYOUT (owner, 2026-10-09, chosen from a clickable prototype) ──────────────────
 *
 *  · Two panels: the six sections on the LEFT at 35%, each with its current values, and the
 *    picked section's settings on the RIGHT, one compact row per setting.
 *  · CALCULATION ORDER: systems bands, interface bands, complexity, importance, N/A, thresholds.
 *  · Each section keeps its own unsaved edits while you move between them (a dot marks it), and
 *    a save re-seeds only the section saved. "Changed" marks saved values that differ from the
 *    package defaults.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_BAND_VALUES_TENTHS,
  DEFAULT_COMPLEXITY_FACTORS,
  DEFAULT_IMPORTANCE_FACTORS,
  DEFAULT_QUADRANT_THRESHOLDS,
  INTERFACES_BOUNDARIES,
  QUADRANT_ORDER,
  SYSTEMS_BOUNDARIES,
  type QuadrantName,
} from '@platform/erp-scoring';
/** D-014 — the initiative is a record picker. */
import { ConfirmDialog, SelectField } from '@platform/ui-kit';
import { ErpScreen } from '../ErpScreen';
import { FormPanel } from '../chrome/FormPanel';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
import { ERP_PROMPT } from '../prompts';
import { erpApi } from '../api';
import { canConfigureScoring } from '../roles';
import { CHIP_INFO, CHIP_NEUTRAL, CHIP_WARNING } from '../chips';
import { QUADRANT_CHIP } from '../quadrantChips';
import { tenthsToText, textToTenths } from '../weights';
import {
  type ErpContext,
  type Initiative,
  type ScoringConfig,
  type ScoringConfigPatch,
  type ScoringDefinitionWarning,
  type ScoringDocumentsPayload,
} from '../types';
import '../chrome/recordList.css';
import './ScoringDefinition.css';

/* ---------------------------------------------------------------------------
 * Contextual help — AUTHORED, in the tone of the precedent's own (`ScoringModel.tsx:17-41`),
 * which is the spec in prose with a `firstTime` that names the one thing to check.
 * ------------------------------------------------------------------------- */

const ScoringDefinitionHelp: Help = {
  title: 'Scoring Definition',
  icon: '🎚️',
  purpose:
    'Defines how this initiative&rsquo;s scores are calculated, in the order the calculation runs: where each systems and interfaces <strong>band</strong> starts and ends and how much <strong>complexity</strong> it adds, the <strong>factor weights</strong> behind complexity and importance, how a <strong>not applicable</strong> capability is treated, and the two <strong>thresholds</strong> that place a unit in a quadrant. The left panel lists the six sections with their current values; the right panel edits the one you pick.',
  doHere: [
    'Pick an <strong>initiative</strong>. Each one has its own definition; until somebody saves one it runs on the <strong>defaults</strong>, and the chip beside the picker says which.',
    'Set the <strong>overlap</strong> and <strong>complexity</strong> thresholds. A unit is &ldquo;high&rdquo; on an axis when it reaches or passes that figure, and the two together decide its quadrant.',
    'Set the <strong>factor weights</strong>. Each pair must total exactly <strong>100.0%</strong> — the running total beside them says how far off you are, and Save stays closed until it is exact.',
    'Set each <strong>band</strong>: where it ends (how many systems or interfaces fall in it) and its <strong>complexity</strong> — how hard a capability in that band is to consolidate. Higher means harder, and lower complexity is what puts a unit in Quick win. The next band starts one above, so no band can be empty, and complexity must rise from band to band. <strong>Add band</strong> splits the open-ended top band; 🗑️ deletes a band and gives its counts to the band above. Each set has between 2 and 10 bands.',
    'Choose the <strong>N/A rule</strong>: a not-applicable capability either counts as 0% and keeps its weight, or is excluded with the remaining weights scaled back to 100% and the coverage shown beside the score.',
    '<strong>Supporting documents</strong> attaches documents from APR&rsquo;s Knowledge Library &rarr; Architecture &amp; Process Diagrams to this scoring model, and marks the ones that are evidence for it. Upload and manage the documents in APR; attaching or detaching here never changes them.',
    'Each section saves on its own, and <strong>nothing else is touched</strong> when it does. Unsaved edits stay in their section while you move between them — a dot marks the section — and <strong>Changed</strong> marks a section whose saved values differ from the defaults.',
  ],
  fits: 'ERP Consolidation &rarr; Scoring Definition',
  firstTime:
    'Check the two thresholds first — they decide which quadrant every unit lands in, and they are the two figures people argue about.',
  sections: [
    {
      type: 'defs',
      label: 'Changing this on a baselined initiative',
      items: [
        {
          term: 'It warns',
          color: '#8c3a00',
          def: 'A definition already in place is what every existing score was calculated from. Changing it <strong>re-scores everything</strong>, and the confirmation says how many units move and how many change quadrant — measured, not estimated.',
        },
        {
          term: 'It never locks',
          color: '#0842a0',
          def: 'The definition is never frozen. There is no way to un-baseline an initiative, so a lock would leave a threshold set wrongly set wrongly for good. The decision is yours, with the consequence in front of you.',
        },
        {
          term: 'Nothing goes stale',
          color: '#0d652d',
          def: 'No score is stored anywhere — every one is calculated when it is read. So the next read already uses the new definition: there is no re-scoring job to wait for and nothing left holding an old number.',
        },
      ],
    },
    {
      type: 'html',
      label: 'What this screen does not do',
      html:
        'It does not set a <strong>quadrant</strong> on a unit — a quadrant is calculated, and both of the figures behind it are overridable at the cell with a reason. It does not carry a colour per quadrant either: those come from the product&rsquo;s own measured palette rather than from configuration.',
    },
  ],
};

/* ---------------------------------------------------------------------------
 * The fields, their human labels, and which section each belongs to.
 *
 * The LABELS are this screen's; the field NAMES are the server's columns, which is what
 * `changedSettings[].field` carries and what the audit trail records. Both are shown in the
 * confirmation dialog — the label so it can be read, the server's own rendered values so it
 * cannot be worded differently from the audit entry.
 * ------------------------------------------------------------------------- */

type ConfigField = keyof ScoringConfigPatch;

const FIELD_LABEL: Record<string, string> = {
  na_option: 'Not-applicable rule',
  overlap_threshold_pct_tenths: 'Overlap threshold',
  complexity_threshold_pct_tenths: 'Complexity threshold',
  systems_factor_weight_tenths: 'Systems factor',
  interfaces_factor_weight_tenths: 'Interfaces factor',
  importance_users_weight_tenths: 'Users factor',
  importance_revenue_weight_tenths: 'Revenue factor',
  systems_band_upper_counts: 'Systems bands — where each ends',
  systems_band_values_pct_tenths: 'Systems bands — complexity',
  interfaces_band_upper_counts: 'Interface bands — where each ends',
  interfaces_band_values_pct_tenths: 'Interface bands — complexity',
};

/**
 * THE DEFAULTS, from the scoring package's own constants — the same numbers `0096` and `0109`
 * default the columns to. Used for two things only: the "Changed" flag beside a section whose
 * saved values differ from them, and the "Default …" note beside a single field that does.
 */
const DEFAULTS: Record<string, number | string | readonly number[]> = {
  na_option: 'zero',
  systems_band_upper_counts: SYSTEMS_BOUNDARIES,
  systems_band_values_pct_tenths: DEFAULT_BAND_VALUES_TENTHS,
  interfaces_band_upper_counts: INTERFACES_BOUNDARIES,
  interfaces_band_values_pct_tenths: DEFAULT_BAND_VALUES_TENTHS,
  overlap_threshold_pct_tenths: DEFAULT_QUADRANT_THRESHOLDS.overlapTenThousandths / 1000,
  complexity_threshold_pct_tenths: DEFAULT_QUADRANT_THRESHOLDS.complexityTenThousandths / 1000,
  systems_factor_weight_tenths: DEFAULT_COMPLEXITY_FACTORS.systemsFactorWeightTenths,
  interfaces_factor_weight_tenths: DEFAULT_COMPLEXITY_FACTORS.interfacesFactorWeightTenths,
  importance_users_weight_tenths: DEFAULT_IMPORTANCE_FACTORS.usersWeightTenths,
  importance_revenue_weight_tenths: DEFAULT_IMPORTANCE_FACTORS.revenueWeightTenths,
};

/** D-022 — between 2 and 10 bands per set, the owner's choice; the server refuses the same. */
const MIN_BANDS = 2;
const MAX_BANDS = 10;

/**
 * THE SIX SECTIONS, IN CALCULATION ORDER — owner, 2026-10-09, choosing from the prototype: the
 * bands feed complexity, the factors weight it and importance, the N/A rule decides what is
 * counted, and the thresholds decide the quadrant last. The left panel numbers them so the order
 * reads as the sequence it is.
 */
type SectionKey =
  | 'systemsBands'
  | 'interfaceBands'
  | 'complexity'
  | 'importance'
  | 'na'
  | 'thresholds'
  | 'documents';

type BandSetKey = 'systems' | 'interfaces';

interface BandSet {
  key: BandSetKey;
  upperColumn: 'systems_band_upper_counts' | 'interfaces_band_upper_counts';
  valuesColumn: 'systems_band_values_pct_tenths' | 'interfaces_band_values_pct_tenths';
  /** Where band 1 starts: a capability has at least one system, and may have no interfaces. */
  floor: number;
  noun: string;
}

const BAND_SETS: Partial<Record<SectionKey, BandSet>> = {
  systemsBands: {
    key: 'systems',
    upperColumn: 'systems_band_upper_counts',
    valuesColumn: 'systems_band_values_pct_tenths',
    floor: 1,
    noun: 'systems',
  },
  interfaceBands: {
    key: 'interfaces',
    upperColumn: 'interfaces_band_upper_counts',
    valuesColumn: 'interfaces_band_values_pct_tenths',
    floor: 0,
    noun: 'interfaces',
  },
};

interface Section {
  key: SectionKey;
  title: string;
  fields: ConfigField[];
  note: ReactNode;
  foot: string;
  /** A pair that must total exactly 100.0% — §6.3 / §6.4, `0096`'s CHECK. */
  pair?: [ConfigField, ConfigField];
}

const SECTIONS: readonly Section[] = [
  {
    key: 'systemsBands',
    title: 'Systems bands',
    fields: ['systems_band_upper_counts', 'systems_band_values_pct_tenths'],
    note: (
      <>
        How many systems support a capability puts it in one of these bands. Set where each band{' '}
        <strong>ends</strong> and how much <strong>complexity</strong> it adds — higher means
        harder to consolidate, and lower complexity is what puts a unit in Quick win. The next
        band starts one above. Add or delete bands — between {MIN_BANDS} and {MAX_BANDS}.
      </>
    ),
    foot: 'Each band must end higher than the one before it, so no band is empty. Complexity must rise from band to band; equal neighbours are allowed — collapsing two bands is a legitimate choice. A falling set is not: it would make a capability supported by six systems look easier to consolidate than one supported by one.',
  },
  {
    key: 'interfaceBands',
    title: 'Interface bands',
    fields: ['interfaces_band_upper_counts', 'interfaces_band_values_pct_tenths'],
    note: (
      <>
        The same for the number of interfaces around a capability. Set where each band{' '}
        <strong>ends</strong> and how much <strong>complexity</strong> it adds — more interfaces,
        harder to consolidate. Add or delete bands — between{' '}
        {MIN_BANDS} and {MAX_BANDS}.
      </>
    ),
    foot: 'Each band must end higher than the one before it. Complexity must rise from band to band, as above.',
  },
  {
    key: 'complexity',
    title: 'Complexity factors',
    fields: ['systems_factor_weight_tenths', 'interfaces_factor_weight_tenths'],
    pair: ['systems_factor_weight_tenths', 'interfaces_factor_weight_tenths'],
    note: (
      <>
        A capability&rsquo;s complexity is its systems band and its interfaces band, weighted by
        these two.
      </>
    ),
    foot: 'The systems and interfaces factors must total exactly 100.0%.',
  },
  {
    key: 'importance',
    title: 'Importance factors',
    fields: ['importance_users_weight_tenths', 'importance_revenue_weight_tenths'],
    pair: ['importance_users_weight_tenths', 'importance_revenue_weight_tenths'],
    note: (
      <>
        Importance is the unit&rsquo;s user count and its revenue, each ranked{' '}
        <strong>across the whole initiative</strong> into five fixed groups worth 20, 40, 60, 80
        and 100%, and weighted by these two. The systems and interface bands do not affect it.
      </>
    ),
    foot: 'The users and revenue factors must total exactly 100.0%.',
  },
  {
    key: 'na',
    title: 'Not-applicable rule',
    fields: ['na_option'],
    note: (
      <>
        What happens to a capability a unit has marked <strong>not applicable</strong>. This is
        the one setting that changes what a score MEANS rather than where its thresholds sit.
      </>
    ),
    foot: 'This does not change what anybody recorded. It changes how a not-applicable capability is counted, which moves every unit that has one.',
  },
  {
    key: 'thresholds',
    title: 'Quadrant thresholds',
    fields: ['overlap_threshold_pct_tenths', 'complexity_threshold_pct_tenths'],
    note: (
      <>
        A unit is <strong>high</strong> on an axis when it reaches or passes the figure below —
        the comparison is inclusive — and the two together decide which of the four quadrants it
        lands in.
      </>
    ),
    foot: 'Moving either threshold moves every unit that sits near it. The confirmation says how many change quadrant before anything is written.',
  },
  /*
   * D-024 — NOT part of the calculation, so it sits after it. The owner: documents "to support
   * the scoring model", pulled from APR's Architecture & Process Diagrams rather than a new
   * library or table. It edits no `scoring_config` field, so it has no Save of its own: attach,
   * evidence and detach each write at once, as the document lists in APR's library do.
   */
  {
    key: 'documents',
    title: 'Supporting documents',
    fields: [],
    note: (
      <>
        Documents from APR&rsquo;s <strong>Knowledge Library &rarr; Architecture &amp; Process
        Diagrams</strong> that support this scoring model. Upload and manage them in APR;
        attaching or detaching here never copies or changes them. Tick <strong>Evidence</strong>{' '}
        for a document the definition is based on.
      </>
    ),
    foot: 'A document deleted in APR stays listed here as no longer available until it is detached.',
  },
];

const FIELD_HINT: Record<string, string> = {
  overlap_threshold_pct_tenths:
    'At or above this, a unit counts as high overlap: the systems already do much the same thing.',
  complexity_threshold_pct_tenths:
    'At or above this, a unit counts as high complexity: converging it is a programme rather than a task.',
  systems_factor_weight_tenths: 'Weight of the systems band in a capability’s complexity.',
  interfaces_factor_weight_tenths: 'Weight of the interfaces band in a capability’s complexity.',
  importance_users_weight_tenths: 'Weight of the unit’s users group in its importance.',
  importance_revenue_weight_tenths: 'Weight of the unit’s revenue group in its importance.',
};

/** Every scalar field, as typed text, so a half-entered "5." stays typable. */
type Form = Record<string, string> & { na_option: string };

/** One band set as typed: `ends` has one fewer entry than `worth`; the last band is open. */
interface BandForm {
  ends: string[];
  worth: string[];
}

type Bands = Record<BandSetKey, BandForm>;

const BAND_COLUMNS: ReadonlySet<string> = new Set([
  'systems_band_upper_counts',
  'systems_band_values_pct_tenths',
  'interfaces_band_upper_counts',
  'interfaces_band_values_pct_tenths',
]);

function seedForm(config: ScoringConfig): Form {
  const form: Form = { na_option: config.na_option };
  for (const field of Object.keys(FIELD_LABEL)) {
    if (field === 'na_option' || BAND_COLUMNS.has(field)) continue;
    const value = (config as unknown as Record<string, unknown>)[field];
    form[field] = typeof value === 'number' ? tenthsToText(value) : '';
  }
  return form;
}

function seedBands(config: ScoringConfig): Bands {
  return {
    systems: {
      ends: config.systems_band_upper_counts.map(String),
      worth: config.systems_band_values_pct_tenths.map(tenthsToText),
    },
    interfaces: {
      ends: config.interfaces_band_upper_counts.map(String),
      worth: config.interfaces_band_values_pct_tenths.map(tenthsToText),
    },
  };
}

/** A typed whole count, or null while the text is not one. */
function textToCount(text: string): number | null {
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

/** A set as numbers, or null while any entry is not yet one. */
function parsedBandSet(band: BandForm): { ends: number[]; worth: number[] } | null {
  const ends = band.ends.map(textToCount);
  const worth = band.worth.map(textToTenths);
  if (ends.some((v) => v === null) || worth.some((v) => v === null)) return null;
  return { ends: ends as number[], worth: worth as number[] };
}

const sameArray = (a: readonly unknown[], b: readonly unknown[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * The patch for one section — only what moved, which is §7b.2's whole premise. A band set is
 * sent WHOLE (both arrays) when either changed, because the server judges a set as a set.
 *
 * A scalar field left EMPTY is left out rather than sent as `null`: every column is `NOT NULL`
 * (`0096`, `0110`), so "no threshold" is not a state the definition has.
 */
function sectionPatch(
  section: Section,
  form: Form,
  bands: Bands,
  config: ScoringConfig,
): ScoringConfigPatch {
  const patch: Record<string, unknown> = {};
  const set = BAND_SETS[section.key];
  if (set) {
    const parsed = parsedBandSet(bands[set.key]);
    if (
      parsed !== null &&
      (!sameArray(parsed.ends, config[set.upperColumn]) ||
        !sameArray(parsed.worth, config[set.valuesColumn]))
    ) {
      patch[set.upperColumn] = parsed.ends;
      patch[set.valuesColumn] = parsed.worth;
    }
    return patch as ScoringConfigPatch;
  }
  for (const field of section.fields) {
    if (field === 'na_option') {
      if (form.na_option !== config.na_option) patch.na_option = form.na_option;
      continue;
    }
    const typed = textToTenths(form[field] ?? '');
    const current = (config as unknown as Record<string, unknown>)[field];
    if (typed !== null && typed !== current) patch[field] = typed;
  }
  return patch as ScoringConfigPatch;
}

const pairTotal = (form: Form, [a, b]: [ConfigField, ConfigField]): number =>
  (textToTenths(form[a] ?? '') ?? 0) + (textToTenths(form[b] ?? '') ?? 0);

/** Every band's range, from what is typed: `[from, to]`, the last `[from, null]`. */
function bandRanges(band: BandForm, set: BandSet): [number | null, number | null][] {
  const out: [number | null, number | null][] = [];
  let from: number | null = set.floor;
  for (const text of band.ends) {
    const to = textToCount(text);
    out.push([from, to]);
    from = to === null ? null : to + 1;
  }
  out.push([from, null]);
  return out;
}

/**
 * What is wrong with a band set, in words — or null. The same rules the server refuses by name
 * (`band_count_out_of_range`, `band_boundaries_must_ascend`, `band_values_must_ascend`), shown
 * live so Save stays closed until they hold, the way the 100.0% pair already behaves.
 */
function bandProblem(band: BandForm, set: BandSet): string | null {
  const n = band.worth.length;
  if (n < MIN_BANDS || n > MAX_BANDS) {
    return `A set has between ${MIN_BANDS} and ${MAX_BANDS} bands.`;
  }
  let previous = set.floor - 1;
  for (let i = 0; i < band.ends.length; i++) {
    const value = textToCount(band.ends[i] ?? '');
    if (value === null) return `Band ${i + 1} must end on a whole number of ${set.noun}.`;
    if (value <= previous) return `Band ${i + 1} must end above ${previous}, or it would be empty.`;
    previous = value;
  }
  for (let i = 0; i < n; i++) {
    const value = textToTenths(band.worth[i] ?? '');
    if (value === null || value < 10 || value > 1000) {
      return `Band ${i + 1} must have a complexity between 1.0% and 100.0%.`;
    }
    const lower = i > 0 ? textToTenths(band.worth[i - 1] ?? '') : null;
    if (lower !== null && value < lower) {
      return `Band ${i + 1} has a lower complexity than band ${i}. Complexity must rise from band to band.`;
    }
  }
  return null;
}

/**
 * ADD splits the open-ended top band: the current top band gets an end as wide as the band
 * below it, and a new open-ended band goes above it at the same worth — so nothing scores
 * differently until somebody changes a number.
 */
function addBand(band: BandForm, set: BandSet): BandForm {
  if (band.worth.length >= MAX_BANDS) return band;
  const ranges = bandRanges(band, set);
  const top = ranges[ranges.length - 1]!;
  const below = ranges[ranges.length - 2];
  const width =
    below && below[0] !== null && below[1] !== null ? Math.max(1, below[1] - below[0] + 1) : 1;
  const end = top[0] === null ? '' : String(top[0] + width - 1);
  return { ends: [...band.ends, end], worth: [...band.worth, band.worth[band.worth.length - 1] ?? ''] };
}

/**
 * DELETE gives the band's counts to the band ABOVE it (its end goes, so the next band starts
 * where this one did). Deleting the top band makes the one below it open-ended instead.
 */
function deleteBand(band: BandForm, index: number): BandForm {
  if (band.worth.length <= MIN_BANDS) return band;
  const ends = [...band.ends];
  if (index === band.worth.length - 1) ends.splice(index - 1, 1);
  else ends.splice(index, 1);
  const worth = band.worth.filter((_, i) => i !== index);
  return { ends, worth };
}

const rangeText = ([from, to]: [number | null, number | null]): string => {
  if (from === null) return '?';
  if (to === null) return `${from}+`;
  return from === to ? String(from) : `${from}–${to}`;
};

/** The one-line summary under a section's name in the left panel, from what is typed. */
function sectionSummary(
  section: Section,
  form: Form,
  bands: Bands,
  docs: ScoringDocumentsPayload | null,
): ReactNode {
  const pct = (field: ConfigField) => `${form[field] || '—'}%`;
  const set = BAND_SETS[section.key];
  if (set) {
    const band = bands[set.key];
    const ranges = bandRanges(band, set);
    return (
      <>
        <b>{band.worth.length} bands</b>
        {ranges.map((range, i) => (
          <span key={i}>
            {' · '}
            {rangeText(range)} → <b>{band.worth[i] || '—'}%</b>
          </span>
        ))}
        {bandProblem(band, set) !== null && <span className="sd-sum-bad"> · needs fixing</span>}
      </>
    );
  }
  if (section.pair) {
    const total = pairTotal(form, section.pair);
    const [a, b] = section.pair;
    return (
      <>
        {FIELD_LABEL[a]?.replace(' factor', '')} <b>{pct(a)}</b> ·{' '}
        {FIELD_LABEL[b]?.replace(' factor', '')} <b>{pct(b)}</b>
        {total !== 1000 && <span className="sd-sum-bad"> · total {tenthsToText(total)}%</span>}
      </>
    );
  }
  if (section.key === 'na') {
    return <b>{form.na_option === 'renormalize' ? 'Excluded and re-scaled' : 'Counts as 0%'}</b>;
  }
  if (section.key === 'documents') {
    if (docs === null) return <>Loading…</>;
    const n = docs.documents.length;
    const evidence = docs.documents.filter((d) => d.is_evidence).length;
    if (n === 0) return <>None attached</>;
    return (
      <>
        <b>{n}</b> attached{evidence > 0 && <> · <b>{evidence}</b> evidence</>}
      </>
    );
  }
  return (
    <>
      Overlap <b>{pct('overlap_threshold_pct_tenths')}</b> · Complexity{' '}
      <b>{pct('complexity_threshold_pct_tenths')}</b>
    </>
  );
}

/** "Default 40.0%" / "Default 15" — the default in the field's own unit. */
const defaultText = (field: string): string => {
  const value = DEFAULTS[field];
  return typeof value === 'number' ? `${tenthsToText(value)}%` : String(value);
};

type Toast = { type: 'success' | 'error'; message: string } | null;

interface Confirmation {
  patch: ScoringConfigPatch;
  section: Section;
  warnings: ScoringDefinitionWarning[];
}

export function ScoringDefinition() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setRoot, rootStyle } = useScreenLock();

  const initiativeId = searchParams.get('initiative') ?? '';

  const [context, setContext] = useState<ErpContext | null>(null);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [config, setConfig] = useState<ScoringConfig | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [bands, setBands] = useState<Bands | null>(null);
  /** D-024 — the documents attached to this definition, and what APR's library still offers. */
  const [docs, setDocs] = useState<ScoringDocumentsPayload | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachPicked, setAttachPicked] = useState<Set<string>>(new Set());
  const [confirmDetach, setConfirmDetach] = useState<{ id: string; name: string } | null>(null);
  const [current, setCurrent] = useState<SectionKey>('systemsBands');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  const [confirm, setConfirm] = useState<Confirmation | null>(null);

  /* auto-dismiss toast — `FrameworksList.tsx:214-219`, 3000 ms. */
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const [contextPayload, listPayload] = await Promise.all([
          erpApi.context(),
          erpApi.initiatives.list({ limit: 200 }),
        ]);
        if (cancelled) return;
        setContext(contextPayload.context);
        setInitiatives(listPayload.initiatives);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadConfig = useCallback(async () => {
    if (!initiativeId) {
      setConfig(null);
      setForm(null);
      setBands(null);
      setDocs(null);
      return;
    }
    setError(null);
    try {
      const payload = await erpApi.scoringConfig.get(initiativeId);
      setConfig(payload.scoring_config);
      setForm(seedForm(payload.scoring_config));
      setBands(seedBands(payload.scoring_config));
      /* Documents are a side panel of the definition: failing to read them must not hide it. */
      setDocs(
        await erpApi.scoringDocuments.list(initiativeId).catch((err: unknown) => {
          setToast({
            type: 'error',
            message: err instanceof Error ? err.message : 'Could not load supporting documents',
          });
          return null;
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the scoring definition');
    }
  }, [initiativeId]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const canModify = canConfigureScoring(context);
  const initiative = initiatives.find((row) => row.id === initiativeId) ?? null;
  const baselined = initiative?.baselined_at != null;

  /** What is saved, as the form would show it — the baseline every "unsaved" mark compares to. */
  const savedForm = useMemo(() => (config === null ? null : seedForm(config)), [config]);
  const savedBands = useMemo(() => (config === null ? null : seedBands(config)), [config]);

  const isDirty = (section: Section): boolean => {
    if (form === null || savedForm === null || bands === null || savedBands === null) return false;
    const set = BAND_SETS[section.key];
    if (set) {
      const a = bands[set.key];
      const b = savedBands[set.key];
      return !sameArray(a.ends, b.ends) || !sameArray(a.worth, b.worth);
    }
    return section.fields.some((field) => (form[field] ?? '') !== (savedForm[field] ?? ''));
  };

  const isCustom = (section: Section): boolean =>
    config !== null &&
    section.fields.some((field) => {
      const stored = (config as unknown as Record<string, unknown>)[field];
      const fallback = DEFAULTS[field];
      return Array.isArray(stored) && Array.isArray(fallback)
        ? !sameArray(stored, fallback)
        : stored !== fallback;
    });

  const sectionOk = (section: Section): boolean => {
    if (form === null || bands === null) return false;
    if (section.pair) return pairTotal(form, section.pair) === 1000;
    const set = BAND_SETS[section.key];
    if (set) return bandProblem(bands[set.key], set) === null;
    return true;
  };

  /* ── The write, and the preview in front of it ─────────────────────────── */

  /**
   * §7b.2 committed. Separate from the preview only so the dialog can call it on Confirm.
   *
   * ONLY THE SAVED SECTION IS RE-SEEDED. Every section keeps its own unsaved edits while you move
   * between them, so re-seeding the whole form from the response would silently throw away what
   * was typed in the other five.
   */
  const commit = useCallback(
    async (patch: ScoringConfigPatch, saved: Section) => {
      /* The precedent re-checks the role inside the handler as well as hiding the control —
         `ScoringModel.tsx:133` — because the two can disagree when a role is revoked while a
         screen is open. The server re-checks regardless, which is the half that decides. */
      if (!canModify) return;
      setBusy(true);
      try {
        const payload = await erpApi.scoringConfig.put(initiativeId, patch);
        const fresh = seedForm(payload.scoring_config);
        const freshBands = seedBands(payload.scoring_config);
        setConfig(payload.scoring_config);
        const set = BAND_SETS[saved.key];
        if (set) {
          setBands((prev) => (prev === null ? freshBands : { ...prev, [set.key]: freshBands[set.key] }));
        } else {
          setForm((prev) => {
            if (prev === null) return fresh;
            const next: Form = { ...prev };
            for (const field of saved.fields) next[field] = fresh[field] ?? '';
            return next;
          });
        }
        setConfirm(null);
        setToast({
          type: 'success',
          message:
            payload.warnings.length === 0
              ? 'Scoring definition saved'
              : 'Scoring definition saved — every score in this initiative now uses it',
        });
      } catch (err) {
        setToast({
          type: 'error',
          message: err instanceof Error ? err.message : 'Could not save the scoring definition',
        });
      } finally {
        setBusy(false);
      }
    },
    [canModify, initiativeId],
  );

  /**
   * SAVE = PREVIEW, THEN DECIDE. §7b.2's `?dryRun=1` validates, builds the proposed row and
   * raises the warning, and writes nothing — so a refusal is reported before anything moves, and
   * a warning is read before anything moves. If there is nothing to say, the commit follows
   * immediately: a confirmation dialog on a change that re-scores nothing is the kind of dialog
   * people learn to click through.
   */
  const saveSection = async (section: Section) => {
    if (!canModify || form === null || bands === null || config === null) return;
    const patch = sectionPatch(section, form, bands, config);
    if (Object.keys(patch).length === 0) {
      setToast({ type: 'error', message: 'Nothing has changed in this section.' });
      return;
    }
    setBusy(true);
    try {
      const preview = await erpApi.scoringConfig.put(initiativeId, patch, { dryRun: true });
      if (preview.warnings.length === 0) {
        setBusy(false);
        await commit(patch, section);
        return;
      }
      setConfirm({ patch, section, warnings: preview.warnings });
    } catch (err) {
      /* The server's own refusal — `factor_weights_must_total_100`, `band_values_must_ascend`,
         `band_boundaries_must_ascend`, `band_count_out_of_range`, `bad_request` naming the field —
         shown as worded (§1.6). */
      setToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'That change was refused',
      });
    } finally {
      setBusy(false);
    }
  };

  /* ── D-024 · attach, evidence, detach — each writes at once and returns the whole list ── */

  const docAction = async (action: () => Promise<ScoringDocumentsPayload>, done: string) => {
    if (!canModify) return;
    setBusy(true);
    try {
      setDocs(await action());
      setToast({ type: 'success', message: done });
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : 'That was refused' });
    } finally {
      setBusy(false);
    }
  };

  const attachPickedDocuments = async () => {
    const ids = [...attachPicked];
    if (ids.length === 0 || !canModify) return;
    setBusy(true);
    try {
      let latest: ScoringDocumentsPayload | null = null;
      for (const id of ids) latest = await erpApi.scoringDocuments.attach(initiativeId, id);
      if (latest !== null) setDocs(latest);
      setAttachOpen(false);
      setAttachPicked(new Set());
      setToast({
        type: 'success',
        message: `${ids.length} document${ids.length === 1 ? '' : 's'} attached`,
      });
    } catch (err) {
      /* Some may have attached before the refusal — re-read rather than guess. */
      setDocs(await erpApi.scoringDocuments.list(initiativeId).catch(() => docs));
      setToast({ type: 'error', message: err instanceof Error ? err.message : 'Could not attach' });
    } finally {
      setBusy(false);
    }
  };

  const discardSection = (section: Section) => {
    const set = BAND_SETS[section.key];
    if (set) {
      setBands((prev) =>
        prev === null || savedBands === null ? prev : { ...prev, [set.key]: savedBands[set.key] },
      );
      return;
    }
    setForm((prev) => {
      if (prev === null || savedForm === null) return prev;
      const next: Form = { ...prev };
      for (const field of section.fields) next[field] = savedForm[field] ?? '';
      return next;
    });
  };

  /** Change one band set in the form — an edit, an add or a delete. */
  const updateBands = (key: BandSetKey, change: (band: BandForm) => BandForm) =>
    setBands((prev) => (prev === null ? prev : { ...prev, [key]: change(prev[key]) }));

  const set = (field: string, value: string) =>
    setForm((prev) => (prev === null ? prev : { ...prev, [field]: value }));

  /* ── Render helpers ──────────────────────────────────────────────────── */

  const changed = (field: ConfigField): boolean =>
    form !== null && savedForm !== null && (form[field] ?? '') !== (savedForm[field] ?? '');

  const offDefault = (field: ConfigField): boolean => {
    if (form === null) return false;
    const typed = textToTenths(form[field] ?? '');
    return typed !== null && typed !== DEFAULTS[field];
  };

  const numberInput = (args: {
    id?: string;
    label: string;
    kind: 'pct' | 'count';
    value: string;
    isChanged: boolean;
    onChange: (value: string) => void;
  }) => (
    <span className={`sd-num${args.kind === 'count' ? ' sd-num--count' : ''}`}>
      <input
        id={args.id}
        className={`sd-input${args.isChanged ? ' is-changed' : ''}`}
        type="number"
        step={args.kind === 'count' ? '1' : '0.1'}
        min={args.kind === 'count' ? '0' : '1'}
        max={args.kind === 'count' ? undefined : '100'}
        inputMode={args.kind === 'count' ? 'numeric' : 'decimal'}
        disabled={!canModify}
        aria-label={args.label}
        value={args.value}
        onChange={(e) => args.onChange(e.target.value)}
      />
    </span>
  );

  const input = (field: ConfigField, label: string) =>
    numberInput({
      id: `erp-sd-${field}`,
      label,
      kind: 'pct',
      value: form?.[field] ?? '',
      isChanged: changed(field),
      onChange: (value) => set(field, value),
    });

  /** One setting on one compact row: label, value, hint. */
  const pctRow = (field: ConfigField) => (
    <div className="sd-row" key={field}>
      <label htmlFor={`erp-sd-${field}`}>{FIELD_LABEL[field]}</label>
      {input(field, FIELD_LABEL[field] ?? field)}
      <span className="sd-hint">
        {FIELD_HINT[field]}
        {offDefault(field) && <span className="sd-default">Default {defaultText(field)}</span>}
      </span>
    </div>
  );

  /*
   * A band set as a table: Band · Number of … (from, to) · Complexity · 🗑️, then "+ Add band".
   * The 🗑️ is the module's own row glyph (`erp-rl-glyph--delete`, `../chrome/recordList.css`),
   * the icon Application Master Data uses everywhere a row can be removed.
   */
  const bandTable = (set: BandSet) => {
    if (bands === null || savedBands === null) return null;
    const band = bands[set.key];
    const saved = savedBands[set.key];
    const sameShape = saved.worth.length === band.worth.length;
    const ranges = bandRanges(band, set);
    const problem = bandProblem(band, set);
    const n = band.worth.length;
    return (
      <>
        <div className="sd-table-wrap">
          <table className="sd-bands">
            <thead>
              <tr>
                <th>Band</th>
                <th>Number of {set.noun}</th>
                <th>Complexity</th>
                {canModify && (
                  <th className="sd-bands-act">
                    <span className="sd-sr-only">Delete</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {ranges.map(([from], i) => {
                const last = i === n - 1;
                return (
                  <tr key={i}>
                    <td>Band {i + 1}</td>
                    <td>
                      <span className="sd-count">
                        <span className="sd-count-from">{from ?? '?'}</span>
                        {last ? (
                          <span className="sd-count-to">and above</span>
                        ) : (
                          <>
                            <span className="sd-count-to">to</span>
                            {numberInput({
                              label: `Band ${i + 1} ends at`,
                              kind: 'count',
                              value: band.ends[i] ?? '',
                              isChanged: !sameShape || band.ends[i] !== saved.ends[i],
                              onChange: (value) =>
                                updateBands(set.key, (b) => ({
                                  ...b,
                                  ends: b.ends.map((v, j) => (j === i ? value : v)),
                                })),
                            })}
                          </>
                        )}
                      </span>
                    </td>
                    <td>
                      {numberInput({
                        label: `Band ${i + 1} complexity`,
                        kind: 'pct',
                        value: band.worth[i] ?? '',
                        isChanged: !sameShape || band.worth[i] !== saved.worth[i],
                        onChange: (value) =>
                          updateBands(set.key, (b) => ({
                            ...b,
                            worth: b.worth.map((v, j) => (j === i ? value : v)),
                          })),
                      })}
                    </td>
                    {canModify && (
                      <td className="sd-bands-act">
                        <button
                          type="button"
                          className="erp-rl-glyph erp-rl-glyph--delete"
                          disabled={busy || n <= MIN_BANDS}
                          title={
                            n <= MIN_BANDS
                              ? `A set needs at least ${MIN_BANDS} bands`
                              : `Delete band ${i + 1}`
                          }
                          aria-label={`Delete band ${i + 1}`}
                          onClick={() => updateBands(set.key, (b) => deleteBand(b, i))}
                        >
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {canModify && (
          <div className="sd-add-row">
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={busy || n >= MAX_BANDS}
              title={n >= MAX_BANDS ? `A set has at most ${MAX_BANDS} bands` : undefined}
              onClick={() => updateBands(set.key, (b) => addBand(b, set))}
            >
              + Add band
            </button>
            <span className="sd-hint">
              {n} of {MAX_BANDS} bands · a new band is added at the top, splitting the open-ended
              band
            </span>
          </div>
        )}
        {problem !== null && (
          <p className="sd-problem" role="alert">
            {problem}
          </p>
        )}
      </>
    );
  };

  const pairTotalBar = (pair: [ConfigField, ConfigField]) => {
    if (form === null) return null;
    const totalTenths = pairTotal(form, pair);
    const exact = totalTenths === 1000;
    const over = totalTenths > 1000;
    return (
      <div className="sd-total">
        <span>Total</span>
        <div className="sd-bar-track">
          <div
            className={`sd-bar-fill${over ? ' is-over' : ''}`}
            style={{ width: `${Math.min(100, Math.max(0, totalTenths / 10))}%` }}
          />
        </div>
        <span className={`sd-bar-pct${exact ? ' is-full' : over ? ' is-over' : ''}`}>
          {tenthsToText(totalTenths)}%
        </span>
        <span>
          {exact
            ? 'The pair totals exactly 100.0%.'
            : 'The pair must total exactly 100.0% before it can be saved.'}
        </span>
      </div>
    );
  };

  /*
   * The attached documents: File · Description · Tags · Evidence · 👁️ 🗑️ — APR's Knowledge
   * Library columns (`KnowledgeLibrary.tsx`) less the ones that describe the upload itself, with
   * the module's own row glyphs (`erp-rl-glyph--view`, `--delete`).
   */
  const documentsTable = () => {
    if (docs === null) return <p className="sd-hint">Loading…</p>;
    if (!docs.source.connected) {
      return (
        <div className="sd-empty">
          <p>Supporting documents come from APR&rsquo;s Knowledge Library.</p>
          <p className="sd-hint">
            This initiative does not use APR, so there is no library to attach from.
          </p>
        </div>
      );
    }
    if (docs.documents.length === 0) {
      return (
        <div className="sd-empty">
          <p>No documents attached yet.</p>
          <p className="sd-hint">
            {canModify
              ? 'Use “+ Attach documents” to choose from APR’s Architecture & Process Diagrams.'
              : 'An administrator can attach documents from APR’s Architecture & Process Diagrams.'}
          </p>
        </div>
      );
    }
    return (
      <div className="sd-table-wrap">
        <table className="sd-bands sd-docs">
          <thead>
            <tr>
              <th>File</th>
              <th>Description</th>
              <th>Tags</th>
              <th>Evidence</th>
              <th className="sd-docs-act">
                <span className="sd-sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {docs.documents.map((doc) => (
              <tr key={doc.document_id}>
                <td>
                  {doc.missing ? (
                    <span className="sd-doc-missing">No longer in APR</span>
                  ) : (
                    <span className="sd-doc-name">{doc.file_name}</span>
                  )}
                </td>
                <td>{doc.description || <span className="sd-hint">—</span>}</td>
                <td>
                  {(doc.tags ?? []).length === 0 ? (
                    <span className="sd-hint">—</span>
                  ) : (
                    <span className="sd-doc-tags">
                      {(doc.tags ?? []).map((tag) => (
                        <span key={tag} className="sd-chip sd-doc-tag">
                          {tag}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={doc.is_evidence}
                    disabled={!canModify || busy || doc.missing}
                    aria-label={`Evidence for this scoring model: ${doc.file_name ?? doc.document_id}`}
                    onChange={(e) =>
                      void docAction(
                        () =>
                          erpApi.scoringDocuments.setEvidence(
                            initiativeId,
                            doc.document_id,
                            e.target.checked,
                          ),
                        e.target.checked ? 'Marked as evidence' : 'No longer marked as evidence',
                      )
                    }
                  />
                </td>
                <td className="sd-docs-act">
                  <span className="sd-doc-actions">
                    {!doc.missing && (
                      <a
                        className="erp-rl-glyph erp-rl-glyph--view"
                        href={erpApi.scoringDocuments.downloadUrl(initiativeId, doc.document_id)}
                        title={`Open ${doc.file_name ?? ''}`}
                        aria-label={`Open ${doc.file_name ?? ''}`}
                      >
                        👁️
                      </a>
                    )}
                    {canModify && (
                      <button
                        type="button"
                        className="erp-rl-glyph erp-rl-glyph--delete"
                        disabled={busy}
                        title="Detach from this scoring model — the document stays in APR"
                        aria-label={`Detach ${doc.file_name ?? doc.document_id}`}
                        onClick={() =>
                          setConfirmDetach({
                            id: doc.document_id,
                            name: doc.file_name ?? 'this document',
                          })
                        }
                      >
                        🗑️
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const section = SECTIONS.find((row) => row.key === current) ?? SECTIONS[0]!;
  const warning = confirm?.warnings[0] ?? null;

  return (
    <ErpScreen>
      <div className="page sd-page" ref={setRoot} style={rootStyle}>
        <HelpPanel help={ScoringDefinitionHelp} />
        <PageHeader
          title="Scoring Definition"
          subtitle="The bands, factor weights, not-applicable rule and thresholds every score in this initiative is calculated from"
        />

        <div className="sd-toolbar">
          <SelectField
            label="Initiative"
            width="lg"
            id="erp-sd-initiative"
            value={initiativeId}
            placeholder={ERP_PROMPT.initiative}
            options={initiatives.map((row) => ({
              value: row.id,
              id: row.name,
              description: row.description,
            }))}
            onChange={(value) => (value ? setSearchParams({ initiative: value }) : setSearchParams({}))}
          />
          {config !== null && (
            <>
              {/* "These are the defaults" and "somebody chose these" are different statements
                  about identical numbers, and §7b.1 returns `source` so a screen can say which. */}
              <span
                className="sd-state-chip"
                style={
                  config.source === 'stored'
                    ? { background: CHIP_INFO.bg, color: CHIP_INFO.fg }
                    : { background: CHIP_NEUTRAL.bg, color: CHIP_NEUTRAL.fg }
                }
                title={
                  config.source === 'stored'
                    ? 'This initiative has its own saved scoring definition'
                    : 'No definition has been saved for this initiative, so it is scoring on the defaults'
                }
              >
                {config.source === 'stored' ? 'Configured' : 'Defaults in force'}
              </span>
              {baselined && (
                <span
                  className="sd-state-chip"
                  style={{ background: CHIP_WARNING.bg, color: CHIP_WARNING.fg }}
                  title="Changing the definition re-scores everything already scored — the confirmation says by how much"
                >
                  Baselined — changes re-score everything
                </span>
              )}
            </>
          )}
        </div>

        {loading ? (
          <div className="sd-container">
            <div className="sd-empty">Loading…</div>
          </div>
        ) : error !== null ? (
          <div className="sd-container">
            <div className="sd-empty">
              <p>⚠️ {error}</p>
              <button type="button" className="btn btn-primary" onClick={() => void loadConfig()}>
                Retry
              </button>
            </div>
          </div>
        ) : initiativeId === '' ? (
          <div className="sd-container">
            <div className="sd-empty">
              <p>Choose an initiative to see how its scores are calculated.</p>
              <p className="sd-hint">
                Each initiative has its own definition. Until one is saved it scores on the
                defaults, and nothing is written until you save a section.
              </p>
            </div>
          </div>
        ) : form === null || bands === null || config === null ? (
          <div className="sd-container">
            <div className="sd-empty">Loading the definition…</div>
          </div>
        ) : (
          /*
           * THE TWO PANELS — owner, 2026-10-09: *"two panels, left is Complexity factors,
           * Importance factors, etc. and the right panel is data that we have to define … the
           * left panel should be 35% of the main screen width"*, then, from the prototype, the
           * left panel WITH VALUES. Each panel scrolls on its own; the header and the picker
           * above them never do.
           */
          <div className="sd-split">
            <nav className="sd-nav" aria-label="Scoring definition sections">
              {SECTIONS.map((row, i) => (
                <button
                  type="button"
                  key={row.key}
                  className="sd-nav-item"
                  aria-current={row.key === current ? 'true' : undefined}
                  onClick={() => setCurrent(row.key)}
                >
                  <span className="sd-nav-head">
                    <span className="sd-nav-step">{i + 1}</span>
                    <span className="sd-nav-title">{row.title}</span>
                    {isDirty(row) && <span className="sd-nav-dot" title="Unsaved changes" />}
                    {isCustom(row) && (
                      <span className="sd-nav-flag" title="The saved values differ from the defaults">
                        Changed
                      </span>
                    )}
                  </span>
                  <span className="sd-nav-sum">{sectionSummary(row, form, bands, docs)}</span>
                </button>
              ))}
            </nav>

            <section className="sd-panel" aria-labelledby="erp-sd-section-title">
              <div className="sd-panel-head">
                <div className="sd-panel-head-text">
                  <h3 id="erp-sd-section-title">{section.title}</h3>
                  <p className="sd-note">{section.note}</p>
                </div>
                {canModify && section.key === 'documents' && docs?.source.connected === true && (
                  <div className="sd-panel-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy || (docs.available?.length ?? 0) === 0}
                      title={
                        (docs.available?.length ?? 0) === 0
                          ? 'Every document in the APR library is already attached'
                          : undefined
                      }
                      onClick={() => {
                        setAttachPicked(new Set());
                        setAttachOpen(true);
                      }}
                    >
                      + Attach documents
                    </button>
                  </div>
                )}
                {canModify && section.key !== 'documents' && (
                  <div className="sd-panel-actions">
                    {isDirty(section) && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={busy}
                        onClick={() => discardSection(section)}
                      >
                        Discard
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy || !isDirty(section) || !sectionOk(section)}
                      title={
                        !sectionOk(section)
                          ? 'Fix the highlighted problem before saving'
                          : !isDirty(section)
                            ? 'Nothing has changed in this section'
                            : undefined
                      }
                      onClick={() => void saveSection(section)}
                    >
                      {busy ? 'Working…' : 'Save'}
                    </button>
                  </div>
                )}
              </div>

              <div className="sd-panel-body">
                {section.key === 'documents' ? (
                  documentsTable()
                ) : BAND_SETS[section.key] ? (
                  bandTable(BAND_SETS[section.key]!)
                ) : section.key === 'na' ? (
                  <>
                    <div className="sd-row sd-row--wide">
                      {/* A two-value enum is not a record, so it stays a native `<select>` —
                          `InitiativeDetailPanel.tsx:404` draws the same line, and D-014 is scoped
                          to record pickers. */}
                      <label htmlFor="erp-sd-na">Treatment</label>
                      <select
                        id="erp-sd-na"
                        className="sd-input sd-select"
                        disabled={!canModify}
                        value={form.na_option}
                        onChange={(e) => set('na_option', e.target.value)}
                      >
                        <option value="zero">Counts as 0% and keeps its weight</option>
                        <option value="renormalize">
                          Excluded, and the rest scaled back to 100% with coverage shown
                        </option>
                      </select>
                    </div>
                    <p className="sd-hint">
                      Under the first rule a unit with half its model not applicable can score at
                      most 50%. Under the second it can still score 100%, and every score says what
                      share of the model it was measured on.
                    </p>
                  </>
                ) : (
                  <>
                    {section.fields.map(pctRow)}
                    {section.pair && pairTotalBar(section.pair)}
                  </>
                )}
                <p className="sd-foot-text">{section.foot}</p>
              </div>
            </section>
          </div>
        )}

        {/* ══ D-024 · choose documents from APR's library. The module's single-width panel. ══ */}
        {attachOpen && docs !== null && (
          <FormPanel
            open
            title="Attach documents"
            subtitle="From APR · Knowledge Library · Architecture & Process Diagrams"
            onRequestClose={() => setAttachOpen(false)}
            onSave={() => void attachPickedDocuments()}
            busy={busy}
            saveLabel={
              attachPicked.size === 0
                ? 'Attach'
                : `Attach ${attachPicked.size} document${attachPicked.size === 1 ? '' : 's'}`
            }
            busyLabel="Attaching…"
            dirty={attachPicked.size > 0}
            discardMessage="Close without attaching?"
          >
            {(docs.available ?? []).length === 0 ? (
              <p className="sd-hint">Every document in that library is already attached.</p>
            ) : (
              <ul className="sd-pick-list">
                {(docs.available ?? []).map((doc) => (
                  <li key={doc.id}>
                    <label className="sd-pick">
                      <input
                        type="checkbox"
                        checked={attachPicked.has(doc.id)}
                        onChange={() =>
                          setAttachPicked((prev) => {
                            const next = new Set(prev);
                            if (next.has(doc.id)) next.delete(doc.id);
                            else next.add(doc.id);
                            return next;
                          })
                        }
                      />
                      <span className="sd-pick-text">
                        <span className="sd-doc-name">{doc.file_name}</span>
                        {doc.description && <span className="sd-hint">{doc.description}</span>}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </FormPanel>
        )}

        {confirmDetach && (
          <ConfirmDialog
            open
            title="Detach this document?"
            message={
              `${confirmDetach.name} will no longer be attached to this scoring model. ` +
              "It stays in APR's Knowledge Library."
            }
            confirmLabel="Detach"
            onConfirm={() => {
              const target = confirmDetach;
              setConfirmDetach(null);
              void docAction(
                () => erpApi.scoringDocuments.detach(initiativeId, target.id),
                'Document detached',
              );
            }}
            onCancel={() => setConfirmDetach(null)}
          />
        )}

        {/* ══ D-015's confirmation. Driven by the PREVIEW, so nothing is written yet. ══════ */}
        {confirm !== null && warning !== null && (
          <div
            className="form-modal"
            onClick={(e) => e.target === e.currentTarget && !busy && setConfirm(null)}
          >
            <div className="form-modal-content" style={{ maxWidth: 720 }}>
              <div className="form-modal-header">
                <h2>A scoring definition is already in place</h2>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setConfirm(null)}
                  aria-label="Close dialog"
                  disabled={busy}
                >
                  ✕
                </button>
              </div>
              <div className="customer-form">
                {/*
                  THE SERVER'S OWN SENTENCE, UNALTERED. §7b.2: `warning` is the contract and
                  `message` is the copy. A screen that rewrote it could word the same change
                  differently from the audit entry the write is about to produce.
                */}
                <p style={{ margin: '0 0 16px', lineHeight: 1.6 }}>{warning.message}</p>

                <table className="sd-change-table">
                  <thead>
                    <tr>
                      <th>Setting</th>
                      <th>Now</th>
                      <th />
                      <th>After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warning.changedSettings.map((change) => (
                      <tr key={change.field}>
                        <td>
                          {FIELD_LABEL[change.field] ?? change.field}
                          <div style={{ fontSize: 11, color: '#90a4ae' }}>{change.field}</div>
                        </td>
                        {/* `beforeDisplay` / `afterDisplay` — rendered by the server, through the
                            same formatter the audit trail uses. Never formatted here. */}
                        <td>{change.beforeDisplay ?? '—'}</td>
                        <td className="sd-change-arrow">→</td>
                        <td>{change.afterDisplay ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {warning.rescoring !== null ? (
                  <div className="sd-impact">
                    <div className="sd-impact-head">
                      {warning.rescoring.unitsScored} assessment unit
                      {warning.rescoring.unitsScored === 1 ? '' : 's'} re-score ·{' '}
                      {warning.rescoring.unitsChangingQuadrant} change quadrant
                    </div>
                    <div className="sd-impact-sub">
                      Measured, not estimated: the initiative was scored twice against the same
                      cells — once on the definition in force, once on the one proposed — and the
                      units whose quadrant moves were counted.
                    </div>
                    <table className="sd-change-table">
                      <thead>
                        <tr>
                          <th>Quadrant</th>
                          <th>Now</th>
                          <th />
                          <th>After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {QUADRANT_ORDER.map((name: QuadrantName) => {
                          const before = warning.rescoring?.quadrantCountsBefore[name] ?? 0;
                          const after = warning.rescoring?.quadrantCountsAfter[name] ?? 0;
                          return (
                            <tr key={name}>
                              <td>
                                <span
                                  className="sd-chip"
                                  style={{
                                    background: QUADRANT_CHIP[name].pair.bg,
                                    color: QUADRANT_CHIP[name].pair.fg,
                                  }}
                                >
                                  {name}
                                </span>
                              </td>
                              <td>{before}</td>
                              <td className="sd-change-arrow">→</td>
                              <td>
                                {after}
                                {after !== before && (
                                  <span style={{ color: '#90a4ae' }}>
                                    {' '}
                                    ({after > before ? '+' : ''}
                                    {after - before})
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="sd-hint">
                    Nothing is scored in this initiative yet, so no figure moves today — but this
                    definition is what every later score will be calculated from.
                  </p>
                )}

                <p className="sd-hint">
                  No score is stored anywhere in this module: every one is calculated when it is
                  read. So the next read already uses the new definition — there is no re-scoring
                  job to wait for, and nothing left holding an old number.
                </p>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setConfirm(null)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy}
                    onClick={() => void commit(confirm.patch, confirm.section)}
                  >
                    {busy ? 'Saving…' : 'Change it and re-score'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div className={`sd-toast sd-toast-${toast.type}`} role="status">
            {toast.type === 'success' ? '✓ ' : '⚠ '}
            {toast.message}
          </div>
        )}
      </div>
    </ErpScreen>
  );
}
